import React, { memo, useState } from 'react';
import LazyLoad from 'react-lazy-load';
import { AnimatePresence, motion } from 'framer-motion';
import classnames from 'classnames';

// Helpers
import { BASE_IMAGE_URI, useOffer } from '../../utils/helpers';

// Hooks
import { useNetwork } from '../../hooks/useNetwork/useNetwork';

// Models
import { IMedia, ICrewMember } from '../../utils/models';

import './Poster.scss';
import { hasProgramAssets, hasNoCovers } from '../../utils/networkMap';
import AccessServiceIcon from '../common/AccessServiceIcon';

export interface IPosterProps {
  media: IMedia | ICrewMember;
  isEvent?: boolean;
}

const Poster = ({ media, isEvent = false }: IPosterProps) => {
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);

  const { title, imageUrl, id, offer, description, accessServices } =
    (media as IMedia) || {};

  const { name, personId } = (media as ICrewMember) || {};

  const formattedPersonId = personId?.slice(7, personId.length);
  const currentOffer = useOffer(offer);

  // Network Logo
  const {
    networkImagePath = '',
    networkDisplayName = '',
    shouldShowNetworkImage = false,
  } = useNetwork(currentOffer) || {};

  let posterUrl = '';

  if (hasNoCovers.includes(offer?.[0]?.providerId)) {
    posterUrl = `${networkImagePath}&sourceType=colorhybrid&width=400`;
  } else if (id) {
    posterUrl = `${imageUrl}?sourceType=colorHybrid&twccategory=Poster&width=219`;
  } else {
    posterUrl = `${BASE_IMAGE_URI}/imageserver/person/${formattedPersonId}?width=250&default=true`;
  }

  const metadataRegex = new RegExp(/title metadata/i);
  const stripFromTitle = (title: string) => {
    return title.replace(metadataRegex, '');
  };

  const renderTitle = () => {
    if (hasProgramAssets.includes(offer?.[0]?.providerId)) {
      return stripFromTitle(description);
    } else {
      return title ? title : name;
    }
  };

  return (
    <figure className="poster">
      <div
        className={classnames({
          'poster__image-wrapper': true,
          event: isEvent,
        })}
      >
        <AnimatePresence>
          {!imageLoaded && (
            <motion.div
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1, ease: 'linear' }}
              className="poster__image  poster__image--placeholder"
            />
          )}
        </AnimatePresence>

        <LazyLoad
          offsetVertical={250}
          offsetHorizontal={250}
          throttle={250}
          debounce={false}
          overflow={true}
        >
          <picture>
            <source
              srcSet={`${posterUrl}&width=400`}
              media="(min-width: 501px)"
            />
            <source
              srcSet={`${posterUrl}&width=300`}
              media="(min-width: 0px) and (max-width: 500px)"
            />
            <img
              width={isEvent ? '382' : '219'}
              height={isEvent ? '214' : '350'}
              src={isEvent ? imageUrl : `${posterUrl}`}
              onLoad={() => setImageLoaded(true)}
              alt={`${renderTitle()?.replace(/"/g, "'")} Poster`}
              className={classnames({
                'poster__image-network': hasNoCovers.includes(
                  offer?.[0]?.providerId
                ),
                poster__image: true,
                'poster__image--loaded': imageLoaded,
                'poster__image--loading': !imageLoaded,
              })}
            />
          </picture>
        </LazyLoad>
      </div>
      <figcaption className="poster__caption">
        <span className="title">{renderTitle()}</span>
        {shouldShowNetworkImage ? (
          <img
            className="network-image"
            src={`${networkImagePath}&sourceType=colorhybrid&width=48`}
            width="48"
            height="48"
            alt={`${networkDisplayName} logo`}
            loading="lazy"
          />
        ) : (
          <div aria-hidden="true" className="network-image" />
        )}
      </figcaption>
      {accessServices && !!accessServices.length && (
        <div className="poster__service-wrapper">
          {accessServices.map((service) => (
            <AccessServiceIcon key={service} serviceType={service} />
          ))}
        </div>
      )}
    </figure>
  );
};

export default memo(Poster);
